// Lightweight localStorage-backed data layer for users and transactions
// Note: This is a demo-only store. No real backend.

const USERS_KEY = 'gl.users.v1';
const TX_KEY = 'gl.transactions.v1';

function uid(prefix = '') {
  return (
    prefix + Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-6)
  );
}

function load(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (e) {
    console.warn('Storage load failed', key, e);
    return fallback;
  }
}

function save(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn('Storage save failed', key, e);
  }
}

function seedIfEmpty() {
  let users = load(USERS_KEY, null);
  let tx = load(TX_KEY, null);
  if (!users || !Array.isArray(users) || users.length === 0) {
    users = [
      { id: uid('u_'), name: 'Alice Johnson', info: 'Prefers USD payouts', phone: '+1 202 111 2222' },
      { id: uid('u_'), name: 'Bob Smith', info: 'Notes: loyal customer', phone: '+1 202 333 4444' },
      { id: uid('u_'), name: 'Carlos Rodriguez', info: 'VIP', phone: '+1 202 555 0101' },
      { id: uid('u_'), name: 'Diana Prince', info: 'Prefers afternoons', phone: '+1 202 777 8888' },
    ];
    save(USERS_KEY, users);
  }
  if (!tx || !Array.isArray(tx)) {
    const [u1, u2] = users;
    const now = Date.now();
    tx = [
      { id: uid('t_'), userId: u1.id, amount: 500, currency: 'USD', description: 'Initial deposit from gold sale', date: now - 1000*60*60*24*10 },
      { id: uid('t_'), userId: u1.id, amount: -120, currency: 'USD', description: 'Partial withdrawal', date: now - 1000*60*60*24*6 },
      { id: uid('t_'), userId: u2.id, amount: 800, currency: 'EUR', description: 'Deposit from gold appraisal', date: now - 1000*60*60*24*4 },
    ];
    save(TX_KEY, tx);
  }
}

seedIfEmpty();

export function listUsers() {
  return load(USERS_KEY, []);
}

export function searchUsers(query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return listUsers();
  return listUsers().filter((u) => {
    const fields = [u.name, u.info, u.phone, u.email]; // include legacy 'email' for compatibility
    return fields.some((v) => (v || '').toLowerCase().includes(q));
  });
}

export function getUser(userId) {
  return listUsers().find((u) => u.id === userId) || null;
}

export function getUserTransactions(userId) {
  const all = load(TX_KEY, []);
  return all
    .filter((t) => t.userId === userId)
    .sort((a, b) => b.date - a.date);
}

export function getAllTransactions() {
  return load(TX_KEY, []).slice().sort((a, b) => b.date - a.date);
}

export function getUserBalance(userId) {
  return getUserTransactions(userId).reduce((sum, t) => sum + Number(t.amount || 0), 0);
}

export function addTransaction(userId, { amount, description, date, currency = 'USD' }) {
  const tx = load(TX_KEY, []);
  const record = {
    id: uid('t_'),
    userId,
    amount: Number(amount),
    currency,
    description: (description || '').trim(),
    date: date ? new Date(date).getTime() : Date.now(),
  };
  tx.push(record);
  save(TX_KEY, tx);
  return record;
}

export function deleteTransaction(txId) {
  const tx = load(TX_KEY, []);
  const filtered = tx.filter((t) => t.id !== txId);
  save(TX_KEY, filtered);
}

export function clearAllDemoData() {
  localStorage.removeItem(USERS_KEY);
  localStorage.removeItem(TX_KEY);
}

export function addUser({ name, info, phone }) {
  const users = load(USERS_KEY, []);
  const user = {
    id: uid('u_'),
    name: (name || '').trim(),
    info: (info || '').trim(),
    phone: (phone || '').trim(),
  };
  users.push(user);
  save(USERS_KEY, users);
  return user;
}

export function updateUser(userId, { name, info, phone }) {
  const users = load(USERS_KEY, []);
  const idx = users.findIndex((u) => u.id === userId);
  if (idx === -1) return null;
  const updated = {
    ...users[idx],
    name: name != null ? String(name).trim() : users[idx].name,
    info: info != null ? String(info).trim() : (users[idx].info ?? users[idx].info),
    phone: phone != null ? String(phone).trim() : users[idx].phone,
  };
  users[idx] = updated;
  save(USERS_KEY, users);
  return updated;
}

export function deleteUser(userId) {
  const users = load(USERS_KEY, []);
  const filtered = users.filter((u) => u.id !== userId);
  save(USERS_KEY, filtered);
  // Also remove user's transactions
  const tx = load(TX_KEY, []);
  const txFiltered = tx.filter((t) => t.userId !== userId);
  save(TX_KEY, txFiltered);
}
